import React, { useEffect, useState } from "react";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import moment from "moment";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaTrash, FaSearch, FaExclamationTriangle, FaCheckCircle, FaMapMarkerAlt, FaBuilding, FaCalendarAlt } from "react-icons/fa";
import { API_URL } from "../constants";
import { fetchClientLocMap, resolveClientLoc } from '../DetailsCompteur/cameraApi';
import Toast from '../DetailsCompteur/Toast';

export default function Home(props) {
  const [boitiersList, setBoitiersList] = useState([]);
  const [filteredBoitiersList, setFilteredBoitiersList] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [filterClient, setFilterClient] = useState("Tous les clients");
  const [filterLocation, setFilterLocation] = useState("Toutes les localisations");
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });
  const navigate = useNavigate();
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : props.user;
  const admin = user.admin;

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast({ type: '', message: '' }), 3000);
  };

  const fetchBoitiers = async () => {
    setLoading(true);
    try {
      const [response, clientLocMap] = await Promise.all([
        axios.get(`${API_URL}/camera/listcamera?id=${user.userId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetchClientLocMap(user.token),
      ]);
      const cameras = response.data.data;

      const formattedBoitiers = cameras.map((camera) => {
        const resolvedClientLoc = resolveClientLoc(camera, clientLocMap);
        return {
          id: camera._id,
          name: camera.nom_camera,
          dateOfService: camera.dateMiseEnService,
          alertCount: camera.dateDepassement ? camera.dateDepassement.length : 0,
          clientName: resolvedClientLoc?.client,
          location: resolvedClientLoc?.localisation,
        };
      });

      setBoitiersList(formattedBoitiers);

      const uniqueClients = [
        "Tous les clients",
        ...new Set(formattedBoitiers.map((b) => b.clientName).filter(Boolean)),
      ];
      const uniqueLocations = [
        "Toutes les localisations",
        ...new Set(formattedBoitiers.map((b) => b.location).filter(Boolean)),
      ];

      setClients(uniqueClients);
      setLocations(uniqueLocations);
    } catch (error) {
      console.error("Erreur lors de la récupération des cameras:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoitiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.userId, user.token]);

  useEffect(() => {
    let filteredList = boitiersList;

    if (searchName) {
      filteredList = filteredList.filter((boitier) =>
        boitier.name && boitier.name.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (filterClient && filterClient !== "Tous les clients") {
      filteredList = filteredList.filter((boitier) =>
        boitier.clientName && boitier.clientName.toLowerCase().includes(filterClient.toLowerCase())
      );
    }

    if (filterLocation && filterLocation !== "Toutes les localisations") {
      filteredList = filteredList.filter((boitier) =>
        boitier.location && boitier.location.toLowerCase().includes(filterLocation.toLowerCase())
      );
    }

    filteredList = [...filteredList].sort((a, b) => b.alertCount - a.alertCount);

    setFilteredBoitiersList(filteredList);
  }, [searchName, filterClient, filterLocation, boitiersList]);

  const navigateToDetails = (boitier) => {
    navigate("/details", { state: { boitier } });
  };

  const handleDelete = async (boitierId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/camera/delete`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        },
        data: { id: boitierId },
      });
      setConfirmingDeleteId(null);
      showToast('success', 'Compteur supprimé.');
      fetchBoitiers();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error.message);
      showToast('error', 'Échec de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 text-gray-900">
      <Header user={user} />
      <div className="flex-grow py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-8">
            Liste des compteurs
          </h1>

          {/* Filtres */}
          <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center shrink-0">
                <FaSearch className="text-white text-sm" />
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Rechercher</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Nom du compteur..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-gray-700"
              >
                {clients.map((client) => (
                  <option key={client} value={client}>
                    {client}
                  </option>
                ))}
              </select>
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-gray-700"
              >
                {locations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Liste */}
          {loading ? (
            <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-12 text-center text-gray-400">
              Chargement des compteurs...
            </div>
          ) : filteredBoitiersList.length === 0 ? (
            <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-12 text-center text-gray-400">
              Aucun compteur ne correspond à votre recherche.
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBoitiersList.map((boitier) => (
                <div
                  key={boitier.id}
                  className="bg-white border border-gray-200 shadow-lg rounded-lg overflow-hidden"
                >
                  {confirmingDeleteId === boitier.id ? (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-red-50 px-6 py-4">
                      <p className="text-sm text-red-700 font-medium">
                        Supprimer le compteur « {boitier.name} » ?
                      </p>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => handleDelete(boitier.id)}
                          disabled={deleting}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                          {deleting ? 'Suppression...' : 'Confirmer'}
                        </button>
                        <button
                          onClick={() => setConfirmingDeleteId(null)}
                          disabled={deleting}
                          className="bg-white border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => navigateToDetails(boitier)}
                      className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-gray-800 truncate mb-2">
                            {boitier.name || 'Sans nom'}
                          </h3>
                          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1.5">
                              <FaCalendarAlt className="text-gray-400" />
                              {boitier.dateOfService
                                ? moment(boitier.dateOfService).format("DD/MM/YYYY")
                                : 'Date inconnue'}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <FaBuilding className="text-gray-400" />
                              {boitier.clientName || 'Pas de client'}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <FaMapMarkerAlt className="text-gray-400" />
                              {boitier.location || 'Pas de localisation'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <span
                            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                              boitier.alertCount > 0
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {boitier.alertCount > 0 ? <FaExclamationTriangle /> : <FaCheckCircle />}
                            {boitier.alertCount > 0
                              ? `${boitier.alertCount} dépassement(s)`
                              : 'Aucun dépassement'}
                          </span>

                          {admin && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmingDeleteId(boitier.id);
                              }}
                              className="text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full p-2 transition-colors"
                              aria-label="Supprimer"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Toast type={toast.type} message={toast.message} />
        </div>
      </div>
      <Footer />
    </div>
  );
}